import React, { useState, useEffect } from 'react';
import PaymentPercentInput from './PaymentPercentInput';

const RipplingPayrollTable = ({ ripplingPayrollTransactions }) => {
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [payrollBreakdowns, setPayrollBreakdowns] = useState([]);
  const [matchedGroups, setMatchedGroups] = useState([]);
  const [unmatchedBankTransactions, setUnmatchedBankTransactions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [grantList, setGrantList] = useState([]);
  const [grantNicknames, setGrantNicknames] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());

  useEffect(() => {
    fetchPayrollBreakdowns();
    fetchGrantList();
  }, []);

  useEffect(() => {
    const sorted = [...ripplingPayrollTransactions].sort(
      (a, b) => new Date(b['Date (UTC)']) - new Date(a['Date (UTC)'])
    );
    setFilteredTransactions(sorted);
  }, [ripplingPayrollTransactions]);

  useEffect(() => {
    buildMatchedGroups(filteredTransactions, payrollBreakdowns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payrollBreakdowns, filteredTransactions]);

  const fetchPayrollBreakdowns = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/show-all`, {
        method: 'GET',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPayrollBreakdowns(data);
      }
    } catch (error) {
      console.error('Error fetching payroll breakdowns:', error);
    }
  };

  const fetchGrantList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGrantList(data);
      }
      // Also fetch nicknames
      const nicknameResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-nicknames`, {
        credentials: 'include'
      });
      if (nicknameResponse.ok) {
        const nicknameData = await nicknameResponse.json();
        setGrantNicknames(nicknameData);
      }
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  };

  // Group payroll breakdowns by pay period into summed categories
  const groupBreakdownsByPeriod = (breakdowns) => {
    const groups = {};
    breakdowns.forEach(b => {
      const payPeriod = b['Pay period'];
      const payDate = b['Pay date'];
      const key = `${payDate}_${payPeriod}`;
      if (!groups[key]) {
        groups[key] = {
          payDate, payPeriod, employees: {},
          totalDirectDeposits: 0, totalTaxes: 0,
          totalDeductionsEmployee: 0, totalDeductionsEmployer: 0,
        };
      }
      const name = b['Name'] || 'Unknown';
      if (!groups[key].employees[name]) {
        groups[key].employees[name] = {
          name, employeeId: b['Employee Id'],
          directDeposits: 0, totalTaxes: 0,
          deductionsEmployee: 0, deductionsEmployer: 0,
          adminTime: b['Admin Time'] || '0',
          scienceTime: b['Science Time'] || '0',
          defaultPayment: b['Default Payment'] || '',
          checked: b['Checked'] || 'No',
          checkedDirectDeposits: 'No',
          checkedTaxes: 'No',
          checkedDeductions: 'No',
          payPeriod: b['Pay period'],
          rows: [],
        };
      }
      const emp = groups[key].employees[name];
      emp.rows.push(b);
      const desc = (b['Description'] || '').toLowerCase();
      const payment = parseFloat(b['Payment'] || 0);
      const rowChecked = b['Checked'] || 'No';
      if (desc === 'direct deposits') {
        emp.directDeposits += payment;
        groups[key].totalDirectDeposits += payment;
        if (rowChecked === 'Yes') emp.checkedDirectDeposits = 'Yes';
      } else if (desc === 'total taxes') {
        emp.totalTaxes += payment;
        groups[key].totalTaxes += payment;
        if (rowChecked === 'Yes') emp.checkedTaxes = 'Yes';
      } else if (desc === 'total deductions - employee') {
        emp.deductionsEmployee += payment;
        groups[key].totalDeductionsEmployee += payment;
        if (rowChecked === 'Yes') emp.checkedDeductions = 'Yes';
      } else if (desc === 'total deductions - employer') {
        emp.deductionsEmployer += payment;
        groups[key].totalDeductionsEmployer += payment;
        if (rowChecked === 'Yes') emp.checkedDeductions = 'Yes';
      }
    });
    return groups;
  };

  // Build matched groups: match bank transactions to payroll period groups
  const buildMatchedGroups = (bankTransactions, breakdowns) => {
    const periodGroups = groupBreakdownsByPeriod(breakdowns);
    const matched = [];
    const usedBankTransactions = new Set();

    Object.values(periodGroups).forEach(group => {
      const periodMatch = {
        payDate: group.payDate,
        payPeriod: group.payPeriod,
        employees: group.employees,
        directDepositsMatch: null,
        taxesMatch: null,
        deductionsMatch: null,
        totalDirectDeposits: group.totalDirectDeposits,
        totalTaxes: group.totalTaxes,
        totalDeductions: group.totalDeductionsEmployee + group.totalDeductionsEmployer,
        totalDeductionsEmployee: group.totalDeductionsEmployee,
        totalDeductionsEmployer: group.totalDeductionsEmployer,
      };

      // Match Direct Deposits: "rippling" or "payroll" (but NOT "tax") in description
      const directDepositAmount = Math.abs(group.totalDirectDeposits);
      if (directDepositAmount > 0) {
        periodMatch.directDepositsMatch = findMatchingBankTransaction(
          bankTransactions, directDepositAmount, ['rippling', 'payroll'], ['tax'], usedBankTransactions
        );
      }

      // Match Total Taxes: "tax" or "rippling" in description
      const taxAmount = Math.abs(group.totalTaxes);
      if (taxAmount > 0) {
        periodMatch.taxesMatch = findMatchingBankTransaction(
          bankTransactions, taxAmount, ['tax', 'rippling'], [], usedBankTransactions
        );
      }

      // Match Total Deductions: "matrix trust" or "matrix" in description
      const deductionAmount = Math.abs(group.totalDeductionsEmployee + group.totalDeductionsEmployer);
      if (deductionAmount > 0) {
        periodMatch.deductionsMatch = findMatchingBankTransaction(
          bankTransactions, deductionAmount, ['matrix trust', 'matrix'], [], usedBankTransactions
        );
      }

      matched.push(periodMatch);
    });

    matched.sort((a, b) => new Date(b.payDate) - new Date(a.payDate));
    setMatchedGroups(matched);

    // Collect unmatched bank transactions
    const unmatched = bankTransactions.filter(t => !usedBankTransactions.has(t.ID));
    setUnmatchedBankTransactions(unmatched);
  };

  // Find a matching bank transaction by amount and description keywords (case-insensitive)
  const findMatchingBankTransaction = (bankTransactions, targetAmount, keywords, excludeKeywords = [], usedSet) => {
    const matchesDesc = (desc) => {
      const d = (desc || '').toLowerCase();
      const hasKey = keywords.some(k => d.includes(k));
      if (!hasKey) return false;
      if (excludeKeywords.length > 0) {
        const hasExclude = excludeKeywords.some(k => d.includes(k));
        if (hasExclude) return false;
      }
      return true;
    };

    // Exact amount match
    const candidates = bankTransactions.filter(t => {
      if (usedSet.has(t.ID)) return false;
      const desc = t.Description || t['Bank Description'] || '';
      if (!matchesDesc(desc)) return false;
      const txAmount = Math.abs(parseFloat(t.Amount || 0));
      return Math.abs(txAmount - targetAmount) < 0.01;
    });

    if (candidates.length > 0) {
      const match = candidates[0];
      usedSet.add(match.ID);
      return { transaction: match, amountMatches: true };
    }

    // Close amount match (within 10%)
    const descCandidates = bankTransactions.filter(t => {
      if (usedSet.has(t.ID)) return false;
      const desc = t.Description || t['Bank Description'] || '';
      return matchesDesc(desc);
    });

    if (descCandidates.length > 0) {
      descCandidates.sort((a, b) => {
        const diffA = Math.abs(Math.abs(parseFloat(a.Amount || 0)) - targetAmount);
        const diffB = Math.abs(Math.abs(parseFloat(b.Amount || 0)) - targetAmount);
        return diffA - diffB;
      });
      const closest = descCandidates[0];
      const closestAmount = Math.abs(parseFloat(closest.Amount || 0));
      if (Math.abs(closestAmount - targetAmount) < targetAmount * 0.1) {
        usedSet.add(closest.ID);
        return {
          transaction: closest,
          amountMatches: false,
          bankAmount: closestAmount,
          breakdownAmount: targetAmount,
        };
      }
    }

    return null;
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleEditDefaultPaymentSubmit = async (empData, updatedPayment) => {
    try {
      for (const row of empData.rows) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/update`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employeeId: row['Employee Id'],
            payPeriod: row['Pay period'],
            defaultPayment: updatedPayment,
            adminTime: parseInt(empData.adminTime) || 0,
            scienceTime: parseInt(empData.scienceTime) || 0,
          }),
        });
        if (!response.ok) {
          const err = await response.text();
          console.error('Failed to update:', err);
        }
      }
      fetchPayrollBreakdowns();
    } catch (error) {
      console.error('Error saving payment:', error);
    }
  };

  const toggleRowSelect = (rowKey) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleConfirmSelected = async () => {
    if (selectedRows.size === 0) {
      alert('Please select rows to confirm.');
      return;
    }

    if (!window.confirm('I confirm that all responses are accurate and true to the best of my knowledge, and that I approve the associated PMS transfers.')) {
      return;
    }

    const invalidRows = [];
    for (const rowKey of selectedRows) {
      // rowKey format: ${payDate}_${payPeriod}_${empName}_${category}
      const parts = rowKey.split('_');
      const category = parts[parts.length - 1];
      for (const group of matchedGroups) {
        const gk = `${group.payDate}_${group.payPeriod}`;
        for (const [name, empData] of Object.entries(group.employees)) {
          const rk = `${gk}_${name}_${category}`;
          if (rk === rowKey) {
            let dp = {};
            if (empData.defaultPayment) {
              try { dp = typeof empData.defaultPayment === 'string' ? JSON.parse(empData.defaultPayment) : empData.defaultPayment; } catch { dp = {}; }
            }
            const total = Object.values(dp).reduce((s, v) => s + (parseFloat(v) || 0), 0);
            // Allow 0% (no grants assigned yet) but reject any other non-100% total
            if (total > 0 && Math.abs(total - 100) > 0.01) {
              invalidRows.push(`${name} (${group.payPeriod} - ${category})`);
            }
          }
        }
      }
    }

    if (invalidRows.length > 0) {
      alert(`The following rows do not have payment percentages totaling 100%:\n\n${invalidRows.join('\n')}\n\nPlease fix the payment allocations before confirming.`);
      return;
    }

    try {
      const checkedIds = [];
      for (const rowKey of selectedRows) {
        const parts = rowKey.split('_');
        const category = parts[parts.length - 1];
        for (const group of matchedGroups) {
          const gk = `${group.payDate}_${group.payPeriod}`;
          for (const [name, empData] of Object.entries(group.employees)) {
            const rk = `${gk}_${name}_${category}`;
            if (rk === rowKey) {
              checkedIds.push({
                payPeriod: empData.payPeriod,
                employeeId: empData.employeeId,
                category: category,
              });
            }
          }
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/confirmChecked`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkedIds }),
      });

      if (response.ok) {
        setSelectedRows(new Set());
        fetchPayrollBreakdowns();
        alert('Payroll entries confirmed successfully.');
      } else {
        alert('Failed to confirm entries.');
      }
    } catch (error) {
      console.error('Error confirming:', error);
      alert('Error confirming entries.');
    }
  };

  const renderBreakdownTable = (group, groupKey, category) => {
    const employees = Object.entries(group.employees);

    return (
      <div className="p-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
              {category === 'directDeposits' && <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Direct Deposits</th>}
              {category === 'taxes' && <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Taxes</th>}
              {category === 'deductions' && (
                <>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Deductions - Emp</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Deductions - Empr</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </>
              )}
              {grantList.map(grant => (
                <th key={grant} className="px-2 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100 border-l border-gray-200">
                  {grantNicknames[grant] ? `${grantNicknames[grant]} (${grant})` : grant}
                </th>
              ))}
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Approve</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees.map(([empName, empData]) => {
              const rowKey = `${groupKey}_${empName}_${category}`;
              const isChecked = category === 'directDeposits' ? empData.checkedDirectDeposits === 'Yes'
                : category === 'taxes' ? empData.checkedTaxes === 'Yes'
                : empData.checkedDeductions === 'Yes';
              let payment = {};
              if (empData.defaultPayment) {
                if (typeof empData.defaultPayment === 'string') {
                  try { payment = JSON.parse(empData.defaultPayment); } catch { payment = {}; }
                } else {
                  payment = empData.defaultPayment;
                }
              }
              const paymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(payment[g]) || 0), 0);

              return (
                <React.Fragment key={rowKey}>
                  <tr className={`transition-colors duration-150 ${isChecked ? 'bg-primary-50 border-l-4 border-primary-400' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-2 text-sm text-gray-900">{empName}</td>
                    {category === 'directDeposits' && (
                      <td className="px-4 py-2 text-sm text-right text-red-600 font-medium">${Math.abs(empData.directDeposits).toFixed(2)}</td>
                    )}
                    {category === 'taxes' && (
                      <td className="px-4 py-2 text-sm text-right text-red-600 font-medium">${Math.abs(empData.totalTaxes).toFixed(2)}</td>
                    )}
                    {category === 'deductions' && (
                      <>
                        <td className="px-4 py-2 text-sm text-right text-red-600 font-medium">${Math.abs(empData.deductionsEmployee).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right text-red-600 font-medium">${Math.abs(empData.deductionsEmployer).toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right text-red-600 font-medium">${Math.abs(empData.deductionsEmployee + empData.deductionsEmployer).toFixed(2)}</td>
                      </>
                    )}
                    {grantList.map(grant => {
                      const percentage = payment[grant] || '0';
                      return (
                        <td key={grant} className="px-1 py-2 text-center border-l border-gray-200">
                          <PaymentPercentInput
                            inputKey={`${rowKey}-${grant}-${percentage}`}
                            grant={grant}
                            grants={grantList}
                            payment={payment}
                            onChange={(updatedPayment) => handleEditDefaultPaymentSubmit(empData, updatedPayment)}
                            disabled={isChecked}
                          />
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-center">
                      {isChecked ? (
                        <span className="text-green-600 font-bold text-lg">✓</span>
                      ) : (
                        <input type="checkbox" className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          checked={selectedRows.has(rowKey)} onChange={() => toggleRowSelect(rowKey)} />
                      )}
                    </td>
                  </tr>
                  {/* Payment total warning sub-row */}
                  {!isChecked && paymentTotal > 0 && Math.abs(paymentTotal - 100) > 0.01 && (
                    <tr className="bg-gray-50">
                      <td colSpan={2 + grantList.length + (category === 'deductions' ? 3 : 1)} className="pl-8 pr-4 py-1 text-xs text-red-600 font-semibold">
                        Payment total: {paymentTotal.toFixed(0)}% (must be 100%)
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMatchBadge = (match, breakdownTotal, label) => {
    if (!match) {
      return (
        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {label}: No bank match (${Math.abs(breakdownTotal).toFixed(2)})
        </span>
      );
    }
    if (match.amountMatches) {
      return (
        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {label}: ✓ Matched (${Math.abs(breakdownTotal).toFixed(2)})
        </span>
      );
    }
    return (
      <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        {label}: ✗ Bank: ${match.bankAmount?.toFixed(2)}, Breakdown: ${match.breakdownAmount?.toFixed(2)}
      </span>
    );
  };

  const renderMatchedGroupRow = (group, matchData, category, label, breakdownTotal) => {
    const groupKey = `${group.payDate}_${group.payPeriod}`;
    const expandKey = `${groupKey}_${category}`;
    const isExpanded = expandedGroups.has(expandKey);
    const bankTx = matchData?.transaction;

    return (
      <React.Fragment key={expandKey}>
        <tr className={`hover:bg-gray-50 transition-colors duration-150 ${matchData?.amountMatches ? 'bg-green-50' : matchData ? 'bg-red-50' : ''}`}>
          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
            {bankTx ? new Date(bankTx['Date (UTC)']).toLocaleDateString() : group.payDate}
          </td>
          <td className="px-4 py-2 text-sm text-gray-900">
            {bankTx ? (bankTx.Description || bankTx['Bank Description'] || '') : `—`}
          </td>
          <td className="px-4 py-2 text-sm font-medium text-red-600 whitespace-nowrap">
            ${bankTx ? Math.abs(parseFloat(bankTx.Amount)).toFixed(2) : Math.abs(breakdownTotal).toFixed(2)}
          </td>
          <td className="px-4 py-2">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{group.payPeriod}</span>
          </td>
          <td className="px-4 py-2">{renderMatchBadge(matchData, breakdownTotal, label)}</td>
          <td className="px-4 py-2">
            {breakdownTotal !== 0 && (
              <button className="btn-secondary text-xs px-3 py-1" onClick={() => toggleGroup(expandKey)}>
                {isExpanded ? 'Hide' : 'View'} Breakdown
              </button>
            )}
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan="6" className="p-0 bg-gray-50">
              {renderBreakdownTable(group, groupKey, category)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          className="btn-primary"
          onClick={handleConfirmSelected}
          disabled={selectedRows.size === 0}
        >
          Submit Approved Records ({selectedRows.size})
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Pay Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Match Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Breakdown</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {matchedGroups.length === 0 && unmatchedBankTransactions.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500 italic">
                    {payrollBreakdowns.length === 0 && filteredTransactions.length === 0
                      ? 'No payroll breakdown data. Import payroll files first.'
                      : 'No matching transactions found.'}
                  </td>
                </tr>
              )}
              {matchedGroups.map(group => (
                <React.Fragment key={`${group.payDate}_${group.payPeriod}`}>
                  {group.totalDirectDeposits !== 0 &&
                    renderMatchedGroupRow(group, group.directDepositsMatch, 'directDeposits', 'Direct Deposits', group.totalDirectDeposits)}
                  {group.totalTaxes !== 0 &&
                    renderMatchedGroupRow(group, group.taxesMatch, 'taxes', 'Taxes', group.totalTaxes)}
                  {group.totalDeductions !== 0 &&
                    renderMatchedGroupRow(group, group.deductionsMatch, 'deductions', 'Deductions', group.totalDeductions)}
                </React.Fragment>
              ))}
              {/* Unmatched bank transactions */}
              {unmatchedBankTransactions.map(t => (
                <tr key={t.ID} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                    {new Date(t['Date (UTC)']).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {t.Description || t['Bank Description'] || ''}
                  </td>
                  <td className="px-4 py-2 text-sm font-medium text-red-600 whitespace-nowrap">
                    ${Math.abs(parseFloat(t.Amount)).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-400">—</td>
                  <td className="px-4 py-2">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Unmatched
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-400">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RipplingPayrollTable;
